import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Database } from "lucide-react";

const TITLES = { almeera_rebuild_from_starlink_v1: "Al-Meera rebuild (copy of Starlink, without Leads)" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const sum = (o) => Object.values(o || {}).reduce((a, b) => a + (Number(b) || 0), 0);

/** Read-only report of one-time tenant migrations — super admins only. */
export default function TenantMigrationsCard() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const isSuper = user?.role === "super_admin";
  useEffect(() => {
    if (!isSuper) return;
    api.get("/admin/tenant-migrations").then((r) => setRows(r.data.migrations || [])).catch(() => setRows([]));
  }, [isSuper]);
  if (!isSuper || !rows?.length) return null;
  return (
    <Card data-testid="tenant-migrations-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2"><Database className="w-4 h-4 text-slate-500" /> Tenant migrations</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.map((m) => {
          const r = m.report || {};
          const del = r.deleted_old_al_meera || {}; const cp = r.copied_from_starlink || {};
          return (
            <div key={m.key} className="border border-slate-200 rounded p-3 text-xs space-y-2" data-testid={`tenant-migration-${m.key}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-slate-800">{TITLES[m.key] || m.key}</span>
                <Badge className={`text-[10px] ${m.status === "done" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}`}
                       data-testid={`tenant-migration-status-${m.key}`}>{m.status === "done" ? `completed ${fmt(m.ran_at)}` : `in progress since ${fmt(m.started_at)}`}</Badge>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Deleted from old Al-Meera · {sum(del)} records</div>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(del).map(([k, v]) => <Badge key={k} variant="outline" className="text-[10px] font-mono text-slate-600">{k}: {v}</Badge>)}
                    {r.users_membership_removed > 0 && <Badge variant="outline" className="text-[10px] text-slate-600">users unlinked: {r.users_membership_removed}</Badge>}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Copied from Starlink · {sum(cp)} records</div>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(cp).map(([k, v]) => <Badge key={k} className="text-[10px] font-mono bg-indigo-50 text-indigo-700 hover:bg-indigo-50">{k}: {v}</Badge>)}
                    <Badge className="text-[10px] bg-emerald-50 text-emerald-700 hover:bg-emerald-50" data-testid={`tenant-migration-users-${m.key}`}>users granted: {r.users_granted ?? 0}</Badge>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
